import React, { useEffect, useContext, useState, ElementType, useMemo } from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { pipe } from 'ramda';
import { repositories } from 'api';
import { simpleProductOperations } from 'store/ducks/simpleProduct';
import { NotificationStateContext } from 'common/components/NotificationProvider';
import { StoreBasicDataApiStructure } from 'common/types';
import WithPaper from 'common/hocs/WithPaper';
import Book from './pages/Book';
import Loader from './components/Loader';
import { CODES } from './common/constants';
import Multimedia from './pages/Multimedia';
import ProductsSet from './pages/ProductsSet';

type Props = {
    fetchSimpleProduct: Function,
    me: StoreBasicDataApiStructure<repositories.MeType>,
    simpleProduct: StoreBasicDataApiStructure<repositories.SimpleProductType>,
    info: StoreBasicDataApiStructure<repositories.InfoType>,
};

type MappingType = {
    me: StoreBasicDataApiStructure<repositories.MeType>,
    simpleProduct: StoreBasicDataApiStructure<repositories.SimpleProductType>,
    info: StoreBasicDataApiStructure<repositories.InfoType>,
};

const Product: React.FC<
    Props &
        RouteComponentProps<{ slug: string }> &
        StoreBasicDataApiStructure<repositories.SimpleProductType>
> = ({
    fetchSimpleProduct,
    match,
    simpleProduct: { data, errorMessage, isLoading, isError },
    me,
    info,
}) => {
    const [redirect, setRedirect] = useState(false);
    const notification = useContext(NotificationStateContext);
    let Component: ElementType = () => null;

    useEffect(() => {
        if (me.data === null || me.isError || info.isError) {
            return;
        }

        const slug = match.params.slug;

        fetchSimpleProduct(slug);
    }, [ fetchSimpleProduct, me.data, me.isError, info.isError, match.params.slug ]);

    useEffect(() => {
        if (errorMessage) {
            setRedirect(true);
            notification.setConfig({
                open: true,
                severity: 'error',
                text: 'Produkt nie został znaleziony.',
            });
        }
    }, [errorMessage]);

    switch (data?.type) {
        case CODES.TYPE_BOOK:
            Component = () => (
                <Book
                    data={data}
                    errorMessage={errorMessage}
                    isError={isError}
                    isLoading={isLoading}
                />
            );
            break;
        case CODES.TYPE_GRANT_APP:
            Component = () => (
                <Multimedia
                    data={data}
                    errorMessage={errorMessage}
                    isError={isError}
                    isLoading={isLoading}
                />
            );
            break;
        case CODES.TYPE_GRANT_SET:
            Component = () => (
                <ProductsSet
                    data={data}
                    errorMessage={errorMessage}
                    isError={isError}
                    isLoading={isLoading}
                />
            );
            break;
    }

    return useMemo(() => {
        if (redirect) return <Redirect to="/products" />;

        return (
            <Loader isLoading={isLoading || me.isLoading || errorMessage}>
                <Component />
            </Loader>
        );
    }, [isLoading, me.isLoading, redirect]);
};

const mapStateToProps = ({ me, simpleProduct, info, }: MappingType) => ({
    me,
    simpleProduct,
    info,
});

const mapDispatchToProps = {
    fetchSimpleProduct: simpleProductOperations.request,
};

export default pipe<any, any, any>(
    connect(mapStateToProps, mapDispatchToProps),
    WithPaper(null),
)(Product);
